// Browser-local generators for random JSON, CSV, dates, and regex-matching
// strings. All randomness uses Math.random() and is meant to run at runtime
// inside client components. Nothing here touches the network or `window`.

// ---------------------------------------------------------------------------
// Shared word banks
// ---------------------------------------------------------------------------

const FIRST_NAMES = [
  'Ada',
  'Grace',
  'Alan',
  'Linus',
  'Katherine',
  'Dennis',
  'Barbara',
  'Tim',
  'Margaret',
  'Ken',
  'Radia',
  'Guido',
  'Anita',
  'Bjarne',
  'Sophie',
  'Marcus',
  'Elena',
  'Omar',
  'Priya',
  'Noah',
];
const LAST_NAMES = [
  'Lovelace',
  'Hopper',
  'Turing',
  'Torvalds',
  'Johnson',
  'Ritchie',
  'Liskov',
  'Hamilton',
  'Thompson',
  'Perlman',
  'Borg',
  'Stroustrup',
  'Nakamura',
  'Silva',
  'Kowalski',
  'Okafor',
];
const CITIES = [
  'London',
  'Berlin',
  'Tokyo',
  'Austin',
  'Toronto',
  'Lisbon',
  'Sydney',
  'Oslo',
  'Amsterdam',
  'Seoul',
  'Dublin',
  'Denver',
  'Madrid',
  'Warsaw',
];
const COUNTRIES = [
  'United States',
  'United Kingdom',
  'Germany',
  'Japan',
  'Canada',
  'Portugal',
  'Australia',
  'Norway',
  'Netherlands',
  'South Korea',
  'Ireland',
  'Spain',
];
const COMPANY_PREFIX = [
  'Nova',
  'Blue',
  'Peak',
  'Bright',
  'North',
  'Quantum',
  'Cedar',
  'Vertex',
  'Orbit',
  'Harbor',
];
const COMPANY_SUFFIX = ['Labs', 'Systems', 'Group', 'Works', 'Tech', 'Digital', 'Studio', 'Co'];
const DOMAINS = ['example.com', 'test.dev', 'mail.co', 'demo.io', 'sample.net'];
const WORDS = [
  'lorem',
  'ipsum',
  'dolor',
  'amet',
  'consectetur',
  'adipiscing',
  'elit',
  'tempor',
  'labore',
  'magna',
  'aliqua',
  'veniam',
  'nostrud',
  'aliquip',
  'commodo',
  'velit',
  'cillum',
  'fugiat',
  'nulla',
  'proident',
];

function randInt(minInclusive: number, maxInclusive: number): number {
  return minInclusive + Math.floor(Math.random() * (maxInclusive - minInclusive + 1));
}

function pick<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

function slug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function hex(length: number): string {
  let out = '';
  for (let i = 0; i < length; i += 1) out += '0123456789abcdef'[randInt(0, 15)];
  return out;
}

function uuidV4(): string {
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${'89ab'[randInt(0, 3)]}${hex(3)}-${hex(12)}`;
}

function randomSentence(): string {
  const length = randInt(5, 12);
  const parts = Array.from({ length }, () => pick(WORDS));
  const text = parts.join(' ');
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
}

// ---------------------------------------------------------------------------
// Field types (shared by the JSON and CSV generators)
// ---------------------------------------------------------------------------

export type FieldType =
  | 'firstName'
  | 'lastName'
  | 'fullName'
  | 'email'
  | 'username'
  | 'uuid'
  | 'int'
  | 'float'
  | 'bool'
  | 'date'
  | 'city'
  | 'country'
  | 'company'
  | 'phone'
  | 'url'
  | 'ipv4'
  | 'color'
  | 'word'
  | 'sentence';

export type FieldTypeOption = { value: FieldType; label: string };

export const FIELD_TYPE_OPTIONS: readonly FieldTypeOption[] = [
  { value: 'firstName', label: 'First name' },
  { value: 'lastName', label: 'Last name' },
  { value: 'fullName', label: 'Full name' },
  { value: 'email', label: 'Email' },
  { value: 'username', label: 'Username' },
  { value: 'uuid', label: 'UUID' },
  { value: 'int', label: 'Integer' },
  { value: 'float', label: 'Float' },
  { value: 'bool', label: 'Boolean' },
  { value: 'date', label: 'Date (ISO)' },
  { value: 'city', label: 'City' },
  { value: 'country', label: 'Country' },
  { value: 'company', label: 'Company' },
  { value: 'phone', label: 'Phone' },
  { value: 'url', label: 'URL' },
  { value: 'ipv4', label: 'IPv4 address' },
  { value: 'color', label: 'Hex color' },
  { value: 'word', label: 'Word' },
  { value: 'sentence', label: 'Sentence' },
];

export type SchemaField = { name: string; type: FieldType };

export const DEFAULT_SCHEMA: readonly SchemaField[] = [
  { name: 'id', type: 'uuid' },
  { name: 'name', type: 'fullName' },
  { name: 'email', type: 'email' },
  { name: 'age', type: 'int' },
  { name: 'active', type: 'bool' },
];

// A single random value. JSON keeps native types; CSV stringifies later.
export function generateFieldValue(type: FieldType): string | number | boolean {
  switch (type) {
    case 'firstName':
      return pick(FIRST_NAMES);
    case 'lastName':
      return pick(LAST_NAMES);
    case 'fullName':
      return `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
    case 'email':
      return `${slug(pick(FIRST_NAMES))}.${slug(pick(LAST_NAMES))}@${pick(DOMAINS)}`;
    case 'username':
      return `${slug(pick(FIRST_NAMES))}${randInt(1, 999)}`;
    case 'uuid':
      return uuidV4();
    case 'int':
      return randInt(1, 1000);
    case 'float':
      return Math.round(Math.random() * 100000) / 100;
    case 'bool':
      return Math.random() < 0.5;
    case 'date': {
      const start = Date.UTC(2000, 0, 1);
      const end = Date.UTC(2030, 11, 31);
      return new Date(start + Math.random() * (end - start)).toISOString().slice(0, 10);
    }
    case 'city':
      return pick(CITIES);
    case 'country':
      return pick(COUNTRIES);
    case 'company':
      return `${pick(COMPANY_PREFIX)} ${pick(COMPANY_SUFFIX)}`;
    case 'phone':
      return `+1-${randInt(200, 999)}-${randInt(200, 999)}-${String(randInt(0, 9999)).padStart(4, '0')}`;
    case 'url':
      return `https://${slug(pick(COMPANY_PREFIX))}.${pick(DOMAINS)}`;
    case 'ipv4':
      return `${randInt(1, 255)}.${randInt(0, 255)}.${randInt(0, 255)}.${randInt(1, 254)}`;
    case 'color':
      return `#${hex(6)}`;
    case 'word':
      return pick(WORDS);
    case 'sentence':
      return randomSentence();
    default:
      return '';
  }
}

function clampCount(count: number): number {
  return Math.min(Math.max(Math.floor(count) || 1, 1), 1000);
}

function normalizeFields(fields: readonly SchemaField[]): SchemaField[] {
  const cleaned = fields
    .map((field, index) => ({
      name: field.name.trim() || `field_${index + 1}`,
      type: field.type,
    }))
    .filter((field) => field.name.length > 0);
  return cleaned.length > 0 ? cleaned : [{ name: 'value', type: 'word' }];
}

export function generateRandomJson(fields: readonly SchemaField[], count: number): string {
  const schema = normalizeFields(fields);
  const total = clampCount(count);
  const rows = Array.from({ length: total }, () => {
    const record: Record<string, string | number | boolean> = {};
    for (const field of schema) record[field.name] = generateFieldValue(field.type);
    return record;
  });
  return JSON.stringify(rows, null, 2);
}

function csvEscape(value: string | number | boolean, delimiter = ','): string {
  const text = String(value);
  // A field has to be quoted when it contains the delimiter in use, not only a comma.
  const needsQuotes = text.includes(delimiter) || text.includes('"') || /[\r\n]/.test(text);
  return needsQuotes ? `"${text.replace(/"/g, '""')}"` : text;
}

export function generateRandomCsv(
  fields: readonly SchemaField[],
  count: number,
  delimiter = ',',
): string {
  const schema = normalizeFields(fields);
  const total = clampCount(count);
  const header = schema.map((field) => csvEscape(field.name, delimiter)).join(delimiter);
  const rows = Array.from({ length: total }, () =>
    schema.map((field) => csvEscape(generateFieldValue(field.type), delimiter)).join(delimiter),
  );
  return [header, ...rows].join('\n');
}

// ---------------------------------------------------------------------------
// Random dates
// ---------------------------------------------------------------------------

export type DateFormat = 'iso' | 'iso-datetime' | 'us' | 'eu' | 'timestamp';

export const DATE_FORMAT_OPTIONS: readonly { value: DateFormat; label: string }[] = [
  { value: 'iso', label: 'ISO date (YYYY-MM-DD)' },
  { value: 'iso-datetime', label: 'ISO date-time' },
  { value: 'us', label: 'US (MM/DD/YYYY)' },
  { value: 'eu', label: 'EU (DD/MM/YYYY)' },
  { value: 'timestamp', label: 'Unix timestamp (seconds)' },
];

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function formatDate(date: Date, format: DateFormat): string {
  const year = date.getUTCFullYear();
  const month = pad2(date.getUTCMonth() + 1);
  const day = pad2(date.getUTCDate());
  switch (format) {
    case 'iso':
      return `${year}-${month}-${day}`;
    case 'iso-datetime':
      return date.toISOString();
    case 'us':
      return `${month}/${day}/${year}`;
    case 'eu':
      return `${day}/${month}/${year}`;
    case 'timestamp':
      return String(Math.floor(date.getTime() / 1000));
    default:
      return date.toISOString();
  }
}

export function generateRandomDates(
  startDate: string,
  endDate: string,
  format: DateFormat,
  count: number,
): { output: string; error: string | null } {
  const startMs = Date.parse(startDate);
  const endMs = Date.parse(endDate);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return { output: '', error: 'Enter valid start and end dates.' };
  }
  const lo = Math.min(startMs, endMs);
  const hi = Math.max(startMs, endMs);
  const total = clampCount(count);
  const lines = Array.from({ length: total }, () =>
    formatDate(new Date(lo + Math.random() * (hi - lo)), format),
  );
  return { output: lines.join('\n'), error: null };
}

// ---------------------------------------------------------------------------
// Regex -> matching string generator
// ---------------------------------------------------------------------------
// Supports a practical subset: literals, ., char classes [a-z0-9] (and
// negated [^...]), escapes \d \w \s \D \W \S \t \n \r, quantifiers
// * + ? {n} {n,m} {n,}, groups ( ) and (?: ), and alternation |. Anchors
// ^ $ \b are accepted but ignored. Backreferences and lookaround are not
// supported and are reported as warnings.

type Range = [number, number];

type RegexNode =
  | { kind: 'seq'; items: RegexNode[] }
  | { kind: 'alt'; options: RegexNode[] }
  | { kind: 'repeat'; node: RegexNode; min: number; max: number }
  | { kind: 'char'; code: number }
  | { kind: 'class'; ranges: Range[]; negated: boolean }
  | { kind: 'any' };

export type RegexParseResult = {
  node: RegexNode | null;
  warnings: string[];
  error: string | null;
};

const UNBOUNDED_CAP = 6; // upper bound used for * + and open-ended {n,}
const PRINTABLE: Range = [0x20, 0x7e];

const CLASS_DIGIT: Range[] = [[0x30, 0x39]];
const CLASS_WORD: Range[] = [
  [0x30, 0x39],
  [0x41, 0x5a],
  [0x61, 0x7a],
  [0x5f, 0x5f],
];
const CLASS_SPACE: Range[] = [
  [0x20, 0x20],
  [0x09, 0x09],
];

class RegexParser {
  private readonly src: string;
  private pos = 0;
  readonly warnings: string[] = [];

  constructor(src: string) {
    this.src = src;
  }

  private peek(): string {
    return this.src[this.pos] ?? '';
  }

  private next(): string {
    return this.src[this.pos++] ?? '';
  }

  private eof(): boolean {
    return this.pos >= this.src.length;
  }

  parse(): RegexNode {
    const node = this.parseAlternation();
    if (!this.eof()) {
      // A stray ) or similar; treat the rest as literals to stay lenient.
      throw new Error(`Unexpected "${this.peek()}" at position ${this.pos}.`);
    }
    return node;
  }

  private parseAlternation(): RegexNode {
    const options: RegexNode[] = [this.parseSequence()];
    while (this.peek() === '|') {
      this.next();
      options.push(this.parseSequence());
    }
    return options.length === 1 ? options[0] : { kind: 'alt', options };
  }

  private parseSequence(): RegexNode {
    const items: RegexNode[] = [];
    while (!this.eof() && this.peek() !== '|' && this.peek() !== ')') {
      const atom = this.parseAtom();
      if (atom) items.push(this.parseQuantifier(atom));
    }
    return { kind: 'seq', items };
  }

  private parseQuantifier(node: RegexNode): RegexNode {
    const ch = this.peek();
    let min: number;
    let max: number;
    if (ch === '*') {
      this.next();
      min = 0;
      max = UNBOUNDED_CAP;
    } else if (ch === '+') {
      this.next();
      min = 1;
      max = UNBOUNDED_CAP;
    } else if (ch === '?') {
      this.next();
      min = 0;
      max = 1;
    } else if (ch === '{') {
      const brace = this.tryParseBrace();
      if (!brace) return node;
      min = brace.min;
      max = brace.max;
    } else {
      return node;
    }
    // Non-greedy markers (*?, +?) do not change what can match.
    if (this.peek() === '?') this.next();
    return { kind: 'repeat', node, min, max };
  }

  private tryParseBrace(): { min: number; max: number } | null {
    const start = this.pos;
    this.next(); // consume {
    let digits = '';
    while (/[0-9]/.test(this.peek())) digits += this.next();
    if (digits === '' && this.peek() !== ',') {
      this.pos = start;
      return null;
    }
    const min = digits === '' ? 0 : Number(digits);
    if (this.peek() === '}') {
      this.next();
      return { min, max: min };
    }
    if (this.peek() === ',') {
      this.next();
      let upper = '';
      while (/[0-9]/.test(this.peek())) upper += this.next();
      if (this.peek() !== '}') {
        this.pos = start;
        return null;
      }
      this.next(); // consume }
      const max = upper === '' ? min + UNBOUNDED_CAP : Number(upper);
      return { min, max: Math.max(min, max) };
    }
    this.pos = start;
    return null;
  }

  private parseAtom(): RegexNode | null {
    const ch = this.peek();
    if (ch === '(') return this.parseGroup();
    if (ch === '[') return this.parseClass();
    if (ch === '\\') return this.parseEscape();
    if (ch === '.') {
      this.next();
      return { kind: 'any' };
    }
    if (ch === '^' || ch === '$') {
      this.next();
      return null; // anchors match empty
    }
    this.next();
    return { kind: 'char', code: ch.codePointAt(0) ?? 0 };
  }

  private parseGroup(): RegexNode {
    this.next(); // consume (
    if (this.peek() === '?') {
      const marker = this.src.slice(this.pos, this.pos + 3);
      if (
        marker.startsWith('?=') ||
        marker.startsWith('?!') ||
        marker.startsWith('?<=') ||
        marker.startsWith('?<!')
      ) {
        this.warnings.push('Lookahead/lookbehind is ignored; it was skipped.');
        // Skip the marker then parse and discard the inner expression.
        this.next(); // ?
        if (this.peek() === '<') this.next();
        this.next(); // = or !
        this.parseAlternation();
        this.expectClose();
        return { kind: 'seq', items: [] };
      }
      if (marker.startsWith('?:')) {
        this.next();
        this.next();
      } else if (marker.startsWith('?<')) {
        // Named group (?<name>...) -> treat as a normal group.
        this.next(); // ?
        this.next(); // <
        while (!this.eof() && this.peek() !== '>') this.next();
        if (this.peek() === '>') this.next();
      }
    }
    const inner = this.parseAlternation();
    this.expectClose();
    return inner;
  }

  private expectClose(): void {
    if (this.peek() === ')') {
      this.next();
    } else {
      throw new Error('Unbalanced group: missing ")".');
    }
  }

  private parseClass(): RegexNode {
    this.next(); // consume [
    let negated = false;
    if (this.peek() === '^') {
      negated = true;
      this.next();
    }
    const ranges: Range[] = [];
    let first = true;
    while (!this.eof() && (this.peek() !== ']' || first)) {
      first = false;
      let lo: number;
      if (this.peek() === '\\') {
        const esc = this.readClassEscape();
        if (Array.isArray(esc)) {
          ranges.push(...esc);
          continue;
        }
        lo = esc;
      } else {
        lo = this.next().codePointAt(0) ?? 0;
      }
      if (this.peek() === '-' && this.src[this.pos + 1] !== ']' && this.pos + 1 < this.src.length) {
        this.next(); // consume -
        let hi: number;
        if (this.peek() === '\\') {
          const esc = this.readClassEscape();
          hi = Array.isArray(esc) ? lo : esc;
        } else {
          hi = this.next().codePointAt(0) ?? lo;
        }
        ranges.push([Math.min(lo, hi), Math.max(lo, hi)]);
      } else {
        ranges.push([lo, lo]);
      }
    }
    if (this.peek() === ']') this.next();
    else throw new Error('Unbalanced character class: missing "]".');
    if (ranges.length === 0) ranges.push([0x20, 0x20]);
    return { kind: 'class', ranges, negated };
  }

  // Returns a code point or an array of ranges (for \d \w \s inside a class).
  private readClassEscape(): number | Range[] {
    this.next(); // consume backslash
    const ch = this.next();
    switch (ch) {
      case 'd':
        return CLASS_DIGIT.map((r) => [...r] as Range);
      case 'w':
        return CLASS_WORD.map((r) => [...r] as Range);
      case 's':
        return CLASS_SPACE.map((r) => [...r] as Range);
      case 'n':
        return 0x0a;
      case 't':
        return 0x09;
      case 'r':
        return 0x0d;
      default:
        return ch.codePointAt(0) ?? 0;
    }
  }

  private parseEscape(): RegexNode | null {
    this.next(); // consume backslash
    const ch = this.next();
    switch (ch) {
      case 'd':
        return { kind: 'class', ranges: CLASS_DIGIT, negated: false };
      case 'D':
        return { kind: 'class', ranges: CLASS_DIGIT, negated: true };
      case 'w':
        return { kind: 'class', ranges: CLASS_WORD, negated: false };
      case 'W':
        return { kind: 'class', ranges: CLASS_WORD, negated: true };
      case 's':
        return { kind: 'class', ranges: CLASS_SPACE, negated: false };
      case 'S':
        return { kind: 'class', ranges: CLASS_SPACE, negated: true };
      case 'n':
        return { kind: 'char', code: 0x0a };
      case 't':
        return { kind: 'char', code: 0x09 };
      case 'r':
        return { kind: 'char', code: 0x0d };
      case 'b':
      case 'B':
        return null; // word boundary matches empty
      default:
        if (/[0-9]/.test(ch)) {
          this.warnings.push('Backreferences are not supported and were treated as literals.');
        }
        return { kind: 'char', code: ch.codePointAt(0) ?? 0 };
    }
  }
}

export function parseRegexPattern(pattern: string): RegexParseResult {
  if (pattern.trim() === '') {
    return { node: null, warnings: [], error: 'Enter a regex pattern.' };
  }
  const parser = new RegexParser(pattern);
  try {
    const node = parser.parse();
    return { node, warnings: dedupe(parser.warnings), error: null };
  } catch (err) {
    return {
      node: null,
      warnings: dedupe(parser.warnings),
      error: err instanceof Error ? err.message : 'Could not parse the pattern.',
    };
  }
}

function dedupe(list: string[]): string[] {
  return Array.from(new Set(list));
}

function rangesFromClass(ranges: Range[], negated: boolean): Range[] {
  if (!negated) return ranges;
  // Build the complement within the printable ASCII band.
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const result: Range[] = [];
  let cursor = PRINTABLE[0];
  for (const [lo, hi] of sorted) {
    if (lo > cursor) result.push([cursor, Math.min(lo - 1, PRINTABLE[1])]);
    cursor = Math.max(cursor, hi + 1);
  }
  if (cursor <= PRINTABLE[1]) result.push([cursor, PRINTABLE[1]]);
  return result.length > 0 ? result : [[0x20, 0x20]];
}

function pickFromRanges(ranges: Range[]): number {
  const total = ranges.reduce((sum, [lo, hi]) => sum + (hi - lo + 1), 0);
  let target = randInt(0, total - 1);
  for (const [lo, hi] of ranges) {
    const size = hi - lo + 1;
    if (target < size) return lo + target;
    target -= size;
  }
  return ranges[0][0];
}

function renderNode(node: RegexNode): string {
  switch (node.kind) {
    case 'seq':
      return node.items.map(renderNode).join('');
    case 'alt':
      return renderNode(pick(node.options));
    case 'char':
      return String.fromCodePoint(node.code);
    case 'any':
      return String.fromCodePoint(randInt(0x41, 0x7a));
    case 'class': {
      const ranges = rangesFromClass(node.ranges, node.negated);
      return String.fromCodePoint(pickFromRanges(ranges));
    }
    case 'repeat': {
      const count = randInt(node.min, node.max);
      let out = '';
      for (let i = 0; i < count; i += 1) out += renderNode(node.node);
      return out;
    }
    default:
      return '';
  }
}

export function generateStringsFromRegex(
  pattern: string,
  count: number,
): { samples: string[]; warnings: string[]; error: string | null } {
  const parsed = parseRegexPattern(pattern);
  if (parsed.error || !parsed.node) {
    return { samples: [], warnings: parsed.warnings, error: parsed.error };
  }
  const total = clampCount(count);
  const samples = Array.from({ length: total }, () => renderNode(parsed.node as RegexNode));
  return { samples, warnings: parsed.warnings, error: null };
}
